import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useWindowSize } from '@utils/hooks'

import { mapToMarker } from '@libs/apryse/utils'
import { Coordinates, MarkerType } from '@libs/apryse/type'

import { SelectPlanFromZone } from '@modules/_shared/components/SelectPlanFromZone'

import { Form, Observation, ProjectFile, ProjectFilePosition, RemoteFile, Task } from '@types'

import { PdfMarkerEditor } from '@modules/_shared/components/PdfMarkerEditor'

import { Container, GlobalStyle } from './LocalizationEdit.styled'

type Props = {
  id?: string
  file?: RemoteFile
  projectFile?: ProjectFile
  projectId: string
  data?: Partial<Observation | Form | Task>
  canEdit?: boolean
  markerType: MarkerType
  onChangeProjectFile: (name: string, projectFile?: ProjectFile) => void
  onAdd?: (newPosition: ProjectFilePosition) => void
  onChange?: (newPosition: ProjectFilePosition) => void
  onRemove: () => void
}

export const LocalizationEdit: React.FC<Props> = ({
  id,
  file,
  projectFile,
  projectId,
  data,
  canEdit = true,
  markerType,
  onChangeProjectFile,
  onAdd,
  onChange,
  onRemove,
}: Props) => {
  const { t } = useTranslation()
  const { isMobile } = useWindowSize()

  const handleAdd = (coordinates: Coordinates) => onAdd?.(coordinates)

  const handleChange = (coordinates: Coordinates) => onChange?.(coordinates)

  const initialMarker = useMemo(() => {
    return data?.projectFilePositions && data?.projectFilePositions.length > 0
      ? mapToMarker(data, markerType)
      : undefined
  }, [data, markerType])

  return (
    <>
      <GlobalStyle />

      <Container id={id}>
        <SelectPlanFromZone
          disabledSearch
          label={t('modules_shared_components_LocalizationEdit_localization_select_label')}
          placeholder={t('modules_shared_components_LocalizationEdit_localization_select_placeholder')}
          projectId={projectId}
          projectFile={projectFile}
          onChangeProjectFile={onChangeProjectFile}
          disabled={!canEdit}
        />

        {file?.url && projectFile && (
          <PdfMarkerEditor
            pdfUrl={file.url}
            initialMarker={initialMarker}
            onAdd={handleAdd}
            onDelete={onRemove}
            onMarkerMoveEnd={handleChange}
            config={{
              fitMode: 'FitPage',
              isReadOnly: true,
              canEdit: canEdit,
              markerType,
              idNumber: data?.idNumber,
              markerColor: data?.trade?.color,
              status: data?.status,
              disabledElements: isMobile ? ['default-top-header'] : undefined,
            }}
          />
        )}
      </Container>
    </>
  )
}
